import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, HandCoins, Landmark, AlertTriangle, Plus, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { money, fmtDate, fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { ClearingCard } from "@/components/ClearingCard";
import { BulkPayLinksButton } from "@/components/BulkPayLinksButton";
import { StatusBadge } from "@/components/StatusBadge";
import { LateFeeButton } from "@/components/LateFeeButton";
import { StatementJobButton } from "@/components/BusinessSettings";
import { Button } from "@/components/ui/button";

const Panel = ({ title, children, testid, action }) => (
  <div className="fade-up rounded-lg border bg-card" data-testid={testid}>
    <div className="flex items-center justify-between border-b px-6 py-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const load = useCallback(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/late-fees/last-run").then((r) => setLastRun(r.data));
  }, []);
  useEffect(load, [load]);

  return (
    <div data-testid="admin-dashboard">
      <PageHeader
        eyebrow="Portfolio overview"
        title="Dashboard"
        subtitle={d ? `As of ${fmtDate(d.as_of)} · US/Eastern` : " "}
        actions={<>
          <LateFeeButton onDone={load} />
          <BulkPayLinksButton lateCount={d?.late_loans} onDone={load} />
          <StatementJobButton onDone={load} />
          <Button asChild data-testid="dashboard-new-loan-button"><Link to="/admin/loans/new"><Plus /> New loan</Link></Button>
        </>}
      />
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard testid="kpi-total-outstanding" label="Total Outstanding" icon={Wallet} value={money(d?.total_outstanding_cents)} hint="Principal balance across all loans" />
        <StatCard testid="kpi-total-collected" label="Total Collected" icon={HandCoins} value={money(d?.total_collected_cents)} hint="All posted payments" style={{ animationDelay: "60ms" }} />
        <StatCard testid="kpi-active-loans" label="Active Loans" icon={Landmark} value={d?.active_loans ?? "—"} hint={d ? `Open loans (incl. late) · ${d.paid_off_loans} paid off` : ""} style={{ animationDelay: "120ms" }} />
        <StatCard testid="kpi-late-loans" label="Late Loans" icon={AlertTriangle} tone={d?.late_loans ? "warn" : undefined} value={d?.late_loans ?? "—"} hint="Installment past due & unpaid" style={{ animationDelay: "180ms" }} />
      </div>

      <ClearingCard />

      <div className="mt-8 grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <Panel title="Needs attention" testid="late-loans-panel"
          action={<Link to="/admin/loans?status=late" className="text-sm font-medium text-primary hover:underline" data-testid="view-late-loans-link">View all late</Link>}>
          {d?.late_list?.length ? (
            <ul className="divide-y">
              {d.late_list.map((l) => (
                <li key={l.id}>
                  <Link to={`/admin/loans/${l.id}`} className="flex items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-muted/50" data-testid={`late-loan-row-${l.loan_number}`}>
                    <div className="min-w-0">
                      <div className="font-medium">{l.borrower_name}</div>
                      <div className="mono text-xs text-muted-foreground">{l.loan_number} · due {fmtDate(l.next_due_date)}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="num text-sm font-semibold">{money(l.payoff_cents)}</span>
                      <StatusBadge status={l.status} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-6 py-10 text-sm text-muted-foreground" data-testid="no-late-loans">No late loans. The portfolio is current.</div>
          )}
        </Panel>

        <Panel title="Recent payments" testid="recent-payments-panel">
          {d?.recent_payments?.length ? (
            <ul className="divide-y">
              {d.recent_payments.map((p) => (
                <li key={p.id}>
                  <Link to={`/admin/loans/${p.loan_id}`} className="flex items-center justify-between gap-3 px-6 py-3.5 transition-colors hover:bg-muted/50">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{p.borrower_name}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(p.date)} · {p.method}</div>
                    </div>
                    <span className="num flex items-center gap-1 text-sm font-semibold">{money(p.amount_cents)} <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" /></span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-6 py-10 text-sm text-muted-foreground">No payments yet.</div>
          )}
        </Panel>
      </div>

      <div className="mt-6 text-xs text-muted-foreground" data-testid="last-late-fee-run">
        {lastRun
          ? `Last late-fee check: ${fmtDateTime(lastRun.ran_at)} ET · ${lastRun.loans_checked} loans checked · ${lastRun.fees_assessed} fees assessed · daily job runs at 12:15 AM ET`
          : "Late-fee check has not run yet · daily job runs at 12:15 AM ET"}
      </div>
    </div>
  );
}
